/* ClientsScreen.js — Client Management with navigation to ClientDashboard
   Connected to /api/clients/* endpoints
   Tabs: All Clients, Active, Projects, Interviews, Analytics
   Full CRUD + navigate to ClientDashboard per client */

import React, { useState, useCallback, useMemo } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView, RefreshControl,
  StatusBar, Alert, Modal, ActivityIndicator, TextInput, Dimensions, Share, Image,
  KeyboardAvoidingView, Platform,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token'); if (t) c.headers.Authorization = 'Bearer ' + t; } catch {} return c; });
const safe = r => r?.data?.data || (Array.isArray(r?.data) ? r.data : []);
const safeObj = r => r?.data || {};

const fmtGBP = v => '£' + (parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

const STATUS_MAP = {
  active: { bg: '#D1FAE5', c: '#059669', l: 'Active' },
  inactive: { bg: '#FEE2E2', c: '#DC2626', l: 'Inactive' },
  prospect: { bg: '#DBEAFE', c: '#2563EB', l: 'Prospect' },
  suspended: { bg: '#FEF3C7', c: '#D97706', l: 'Suspended' },
};

const INDUSTRIES = ['Technology', 'Construction', 'Consulting', 'Marketing', 'Finance', 'Healthcare', 'Manufacturing', 'Retail', 'Legal', 'Other'];

export default function ClientsScreen({ navigation, route }) {
  const companyId = parseInt(route?.params?.company_id || route?.params?.companyProfile?.id, 10) || 0;

  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');

  // API Data
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [dashStats, setDashStats] = useState({});

  // Modals
  const [showAddClient, setShowAddClient] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selected, setSelected] = useState(null);

  // Form
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', city: '', industry: 'Technology', notes: '' });
  const uf = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ═══════════════════════════════════════════════════════
  // API — Connected to /api/clients/*
  // ═══════════════════════════════════════════════════════
  const loadAll = async () => {
    setIsLoading(true);
    try {
      const cid = await AsyncStorage.getItem('company_id') || String(companyId);
      const p = { company_id: parseInt(cid) };

      // Company profile
      try { const c = await AsyncStorage.getItem('company_profile'); if (c) { const pr = JSON.parse(c); if (pr.logo) setCompanyLogo(pr.logo); if (pr.name) setCompanyName(pr.name); } } catch {}

      // Dashboard endpoint — gets everything
      const r = await api.get('/api/data/clients', { params: p });
      if (r.data?.success) {
        setClients(r.data.data || []);
        setProjects(r.data.projects || []);
        setInterviews(r.data.upcoming_interviews || []);
        setContracts(r.data.recent_contracts || []);
        setDashStats(r.data.stats || {});
      } else {
        // Fallback: try list endpoint
        const listR = await api.get('/api/data/clients', { params: p });
        if (listR.data?.success) setClients(listR.data.data || []);
      }
    } catch (e) { if (typeof __DEV__ !== 'undefined' && __DEV__) console.log('[ClientsScreen] load failed:', e?.message); }
    finally { setIsLoading(false); setRefreshing(false); }
  };

  useFocusEffect(useCallback(() => { loadAll(); }, []));
  const onRefresh = () => { setRefreshing(true); loadAll(); };

  // Stats
  const stats = useMemo(() => ({
    total: dashStats.total_clients || clients.length,
    active: dashStats.active_clients || clients.filter(c => c.status === 'active').length,
    projects: dashStats.total_projects || projects.length,
    interviews: dashStats.upcoming_interviews || interviews.length,
    contracts: dashStats.total_contracts || contracts.length,
    revenue: dashStats.total_revenue || 0,
    budget: dashStats.total_budget || 0,
  }), [dashStats, clients, projects, interviews, contracts]);

  // Filtered
  const filtered = useMemo(() => {
    let list = [...clients];
    if (activeTab === 'active') list = list.filter(c => c.status === 'active');
    if (activeTab === 'inactive') list = list.filter(c => c.status !== 'active');
    if (searchQuery) { const q = searchQuery.toLowerCase(); list = list.filter(c => JSON.stringify(c).toLowerCase().includes(q)); }
    return list;
  }, [clients, activeTab, searchQuery]);

  // ── Actions ──
  const handleAddClient = async () => {
    if (!form.name?.trim()) { Alert.alert('Required', 'Enter client name'); return; }
    try {
      const cid = await AsyncStorage.getItem('company_id') || String(companyId);
      const r = await api.post('/api/data/clients', { ...form, company_id: parseInt(cid) });
      if (r.data?.success) {
        Alert.alert('Created', 'Client created successfully');
        setShowAddClient(false);
        setForm({ name: '', email: '', phone: '', company: '', city: '', industry: 'Technology', notes: '' });
        loadAll();
      } else { Alert.alert('Error', r.data?.error || 'Failed'); }
    } catch (e) { Alert.alert('Error', e.message); }
  };

  const handleDeleteClient = (client) => {
    Alert.alert('Deactivate Client', `Deactivate "${client.name}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Deactivate', style: 'destructive', onPress: async () => {
        try { await api.delete(`/api/data/clients/${client.id}`); loadAll(); setShowDetail(false); } catch {}
      }},
    ]);
  };

  // ── Navigate to Client Dashboard ──
  const openClientDashboard = (client) => {
    setShowDetail(false);
    navigation.navigate('ClientDashboard', {
      clientId: client.id,
      clientName: client.name,
      clientData: client,
      companyProfile: route?.params?.companyProfile,
      companyLogo,
      companyName,
      subscription: route?.params?.subscription,
    });
  };

  const handleExport = () => {
    const lines = [`CLIENT LIST — ${companyName}`, `Date: ${fmtDate(new Date())}`, `Total: ${stats.total} clients`, '',
      ...clients.map(c => `${c.name} | ${c.company || ''} | ${c.email || ''} | ${c.industry || ''} | ${c.status}`)];
    Share.share({ message: lines.join('\n') });
  };

  // ═══════════════════════════════════════════════════════
  // RENDERS
  // ═══════════════════════════════════════════════════════
  const getSt = s => STATUS_MAP[s] || STATUS_MAP.active;

  const TABS = [
    { k: 'all', l: 'All Clients', i: 'account-group', n: stats.total },
    { k: 'active', l: 'Active', i: 'check-circle', n: stats.active },
    { k: 'projects', l: 'Projects', i: 'briefcase', n: stats.projects },
    { k: 'interviews', l: 'Interviews', i: 'calendar-clock', n: stats.interviews },
    { k: 'analytics', l: 'Analytics', i: 'chart-line' },
  ];

  const renderHeader = () => (
    <LinearGradient colors={['#0e4e96', '#1e7be2']} style={s.header}>
      <View style={s.headerRow}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={s.hBtn}><Ionicons name="arrow-back" size={22} color="#FFF" /></TouchableOpacity>
        {companyLogo ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 28, height: 28, borderRadius: 8, marginLeft: 6 }} /> : null}
        <View style={{ flex: 1, marginLeft: 8 }}><Text style={s.hTitle}>{companyName || 'Clients'}</Text>
          <Text style={{ color: '#93C5FD', fontSize: 11 }}>Client Management · {stats.total} clients</Text></View>
        <TouchableOpacity style={s.hBtn} onPress={handleExport}><MaterialCommunityIcons name="export-variant" size={20} color="#FFF" /></TouchableOpacity>
        <TouchableOpacity style={[s.hBtn, { backgroundColor: '#FFF30', marginLeft: 6 }]} onPress={() => setShowAddClient(true)}>
          <MaterialCommunityIcons name="plus" size={22} color="#FFF" /></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderKPIs = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.kpiBar} contentContainerStyle={{ paddingHorizontal: 16 }}>
      {[{ l: 'Total', v: String(stats.total), c: '#0e4e96', i: 'account-group' },
        { l: 'Active', v: String(stats.active), c: '#10B981', i: 'check-circle' },
        { l: 'Projects', v: String(stats.projects), c: '#6366F1', i: 'briefcase' },
        { l: 'Interviews', v: String(stats.interviews), c: '#D97706', i: 'calendar-clock' },
        { l: 'Contracts', v: String(stats.contracts), c: '#0891B2', i: 'file-document' },
        { l: 'Revenue', v: fmtGBP(stats.revenue), c: '#10B981', i: 'cash' },
      ].map((k, i) => (<View key={i} style={s.kpiItem}><MaterialCommunityIcons name={k.i} size={16} color={k.c} />
        <Text style={[s.kpiVal, { color: k.c }]}>{k.v}</Text><Text style={s.kpiLbl}>{k.l}</Text></View>))}
    </ScrollView>
  );

  const renderTabs = () => (
    <View style={s.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 8 }}>
      {TABS.map(t => (<TouchableOpacity key={t.k} style={[s.tab, activeTab === t.k && s.activeTab]} onPress={() => setActiveTab(t.k)}>
        <MaterialCommunityIcons name={t.i} size={16} color={activeTab === t.k ? '#0e4e96' : '#94A3B8'} />
        <Text style={[s.tabTxt, activeTab === t.k && { color: '#0e4e96', fontWeight: '700' }]}>{t.l}</Text>
        {t.n > 0 && <View style={{ backgroundColor: '#0e4e9620', borderRadius: 8, paddingHorizontal: 4, marginLeft: 3 }}>
          <Text style={{ fontSize: 9, fontWeight: '700', color: '#0e4e96' }}>{t.n}</Text></View>}
      </TouchableOpacity>))}
    </ScrollView></View>
  );

  // ── Client List ──
  const renderClients = () => (
    <View>
      <View style={{ flexDirection: 'row', alignItems: 'center', margin: 16, marginBottom: 8 }}>
        <View style={s.searchRow}><MaterialCommunityIcons name="magnify" size={18} color="#94A3B8" />
          <TextInput style={s.searchInput} placeholder="Search clients..." placeholderTextColor="#94A3B8" value={searchQuery} onChangeText={setSearchQuery} /></View>
        <TouchableOpacity style={s.addBtn} onPress={() => setShowAddClient(true)}><MaterialCommunityIcons name="plus" size={20} color="#FFF" /></TouchableOpacity>
      </View>
      {filtered.length === 0 ? (
        <View style={{ alignItems: 'center', padding: 40 }}><MaterialCommunityIcons name="account-group" size={48} color="#CBD5E1" />
          <Text style={{ color: '#64748B', marginTop: 12, fontSize: 15 }}>No clients found</Text>
          <TouchableOpacity style={[s.addBtn, { paddingHorizontal: 20, marginTop: 12 }]} onPress={() => setShowAddClient(true)}>
            <MaterialCommunityIcons name="plus" size={16} color="#FFF" /><Text style={{ color: '#FFF', fontWeight: '600', marginLeft: 4 }}>Add Client</Text></TouchableOpacity></View>
      ) : filtered.map((c, i) => {
        const st2 = getSt(c.status);
        return (
          <TouchableOpacity key={c.id || i} style={s.card} onPress={() => { setSelected(c); setShowDetail(true); }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={[s.avatar, { backgroundColor: '#0e4e96' }]}>
                <Text style={s.avatarTxt}>{(c.name || '?').substring(0, 2).toUpperCase()}</Text></View>
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={s.cardName}>{c.name}</Text>
                <Text style={s.cardMeta}>{c.company ? c.company + ' · ' : ''}{c.industry || ''}{c.city ? ' · ' + c.city : ''}</Text>
                {c.email && <Text style={[s.cardMeta, { color: '#0e4e96' }]}>{c.email}</Text>}
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <View style={[s.badge, { backgroundColor: st2.bg }]}>
                  <Text style={[s.badgeTxt, { color: st2.c }]}>{st2.l}</Text></View>
                {(c.project_count > 0 || c.interview_count > 0) && (
                  <Text style={{ fontSize: 10, color: '#94A3B8', marginTop: 4 }}>
                    <Text>{c.project_count > 0 ? `${c.project_count} proj` : ''}{c.interview_count > 0 ? ` · ${c.interview_count} int` : ''}</Text>
                  </Text>
                )}
              </View>
            </View>
            {/* Quick action: Open Dashboard */}
            <TouchableOpacity style={s.dashBtn} onPress={() => openClientDashboard(c)}>
              <MaterialCommunityIcons name="view-dashboard" size={14} color="#0e4e96" />
              <Text style={s.dashBtnTxt}>Open Dashboard</Text>
              <Ionicons name="chevron-forward" size={14} color="#0e4e96" />
            </TouchableOpacity>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  // ── Projects Tab ──
  const renderProjects = () => (
    <View style={{ padding: 16 }}>
      <Text style={s.secTitle}>Client Projects ({projects.length})</Text>
      {projects.length === 0 ? <Text style={{ color: '#94A3B8', textAlign: 'center', padding: 20 }}>No projects</Text>
      : projects.map((p, i) => (
        <View key={p.id || i} style={s.card}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <View style={{ flex: 1 }}><Text style={s.cardName}>{p.project_name}</Text>
              <Text style={s.cardMeta}>{p.client_name} · {p.priority || 'medium'}</Text></View>
            <View style={[s.badge, { backgroundColor: p.status === 'active' ? '#D1FAE5' : '#FEF3C7' }]}>
              <Text style={[s.badgeTxt, { color: p.status === 'active' ? '#059669' : '#D97706' }]}>{p.status}</Text></View>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: 12, color: '#64748B' }}>Budget: {fmtGBP(p.budget)}</Text>
            <Text style={{ fontSize: 12, color: '#10B981' }}>{p.completion_percentage || 0}% complete</Text>
          </View>
          {p.budget > 0 && <View style={{ height: 4, backgroundColor: '#E2E8F0', borderRadius: 2, marginTop: 6 }}>
            <View style={{ width: `${p.completion_percentage || 0}%`, height: '100%', backgroundColor: '#0e4e96', borderRadius: 2 }} /></View>}
        </View>
      ))}
    </View>
  );

  // ── Interviews Tab ──
  const renderInterviews = () => (
    <View style={{ padding: 16 }}>
      <Text style={s.secTitle}>Upcoming Interviews ({interviews.length})</Text>
      {interviews.length === 0 ? <Text style={{ color: '#94A3B8', textAlign: 'center', padding: 20 }}>No upcoming interviews</Text>
      : interviews.map((iv, i) => (
        <View key={iv.id || i} style={s.card}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: '#EFF6FF', justifyContent: 'center', alignItems: 'center', marginRight: 12 }}>
              <Text style={{ fontSize: 18, fontWeight: '700', color: '#0e4e96' }}>{new Date(iv.scheduled_date).getDate()}</Text>
              <Text style={{ fontSize: 10, fontWeight: '600', color: '#1e7be2' }}>{new Date(iv.scheduled_date).toLocaleDateString('en-GB', { month: 'short' })}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.cardName}>{iv.title}</Text>
              <Text style={s.cardMeta}>{iv.client_name} · {iv.interview_type || 'client'}</Text>
              <Text style={s.cardMeta}>{iv.scheduled_time || ''}{iv.client_representative ? ' · ' + iv.client_representative : ''}</Text>
            </View>
            <View style={[s.badge, { backgroundColor: '#DBEAFE' }]}>
              <Text style={[s.badgeTxt, { color: '#2563EB' }]}>{iv.status}</Text></View>
          </View>
        </View>
      ))}
    </View>
  );

  // ── Analytics Tab ──
  const renderAnalytics = () => {
    const byIndustry = {};
    clients.forEach(c => { const ind = c.industry || 'Other'; byIndustry[ind] = (byIndustry[ind] || 0) + 1; });
    return (
      <View style={{ padding: 16 }}>
        <View style={s.card}>
          <Text style={s.secTitle}>Overview</Text>
          {[{ l: 'Total Clients', v: String(stats.total), c: '#0e4e96' },
            { l: 'Active Clients', v: String(stats.active), c: '#10B981' },
            { l: 'Active Projects', v: String(stats.projects), c: '#6366F1' },
            { l: 'Total Revenue', v: fmtGBP(stats.revenue), c: '#10B981' },
            { l: 'Total Budget', v: fmtGBP(stats.budget), c: '#0e4e96' },
            { l: 'Upcoming Interviews', v: String(stats.interviews), c: '#D97706' },
            { l: 'Active Contracts', v: String(stats.contracts), c: '#0891B2' },
          ].map((r, i) => (<View key={i} style={s.row}><Text style={s.rowLbl}>{r.l}</Text><Text style={[s.rowVal, { color: r.c }]}>{r.v}</Text></View>))}
        </View>
        <View style={s.card}>
          <Text style={s.secTitle}>By Industry</Text>
          {Object.entries(byIndustry).sort((a, b) => b[1] - a[1]).map(([ind, count]) => {
            const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
            return (<View key={ind} style={{ marginBottom: 8 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                <Text style={{ fontSize: 13, color: '#334155' }}>{ind}</Text>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0e4e96' }}>{count} ({pct}%)</Text></View>
              <View style={{ height: 5, backgroundColor: '#E2E8F0', borderRadius: 3 }}>
                <View style={{ width: `${pct}%`, height: '100%', backgroundColor: '#0e4e96', borderRadius: 3 }} /></View>
            </View>);
          })}
        </View>
      </View>
    );
  };

  const renderContent = () => { switch (activeTab) {
    case 'all': case 'active': case 'inactive': return renderClients();
    case 'projects': return renderProjects();
    case 'interviews': return renderInterviews();
    case 'analytics': return renderAnalytics();
    default: return renderClients();
  }};

  // ── Add Client Modal ──
  const renderAddClientModal = () => (
    <Modal visible={showAddClient} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAddClient(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
        <View style={s.modalH}><TouchableOpacity onPress={() => setShowAddClient(false)}><Ionicons name="close" size={24} color="#64748B" /></TouchableOpacity>
          <Text style={s.modalTitle}>New Client</Text><View style={{ width: 24 }} /></View>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            <Text style={s.formLbl}>Client Name *</Text>
            <TextInput style={s.formInput} value={form.name} onChangeText={v => uf('name', v)} placeholder="e.g. TechCorp Solutions" />
            <Text style={s.formLbl}>Company</Text>
            <TextInput style={s.formInput} value={form.company} onChangeText={v => uf('company', v)} placeholder="e.g. TechCorp Ltd" />
            <Text style={s.formLbl}>Email</Text>
            <TextInput style={s.formInput} value={form.email} onChangeText={v => uf('email', v)} placeholder="contact@company.com" keyboardType="email-address" />
            <Text style={s.formLbl}>Phone</Text>
            <TextInput style={s.formInput} value={form.phone} onChangeText={v => uf('phone', v)} placeholder="+44 20 1234 5678" keyboardType="phone-pad" />
            <Text style={s.formLbl}>City</Text>
            <TextInput style={s.formInput} value={form.city} onChangeText={v => uf('city', v)} placeholder="London" />
            <Text style={s.formLbl}>Industry</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 10 }}>
              {INDUSTRIES.map(ind => (<TouchableOpacity key={ind} style={[s.chip, form.industry === ind && s.chipActive]} onPress={() => uf('industry', ind)}>
                <Text style={[s.chipTxt, form.industry === ind && { color: '#FFF' }]}>{ind}</Text></TouchableOpacity>))}
            </ScrollView>
            <Text style={s.formLbl}>Notes</Text>
            <TextInput style={[s.formInput, { height: 60, textAlignVertical: 'top' }]} value={form.notes} onChangeText={v => uf('notes', v)} placeholder="Additional info..." multiline />
            <TouchableOpacity style={s.submitBtn} onPress={handleAddClient}>
              <MaterialCommunityIcons name="check" size={18} color="#FFF" /><Text style={s.submitBtnTxt}>Create Client</Text></TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  // ── Detail Modal ──
  const renderDetailModal = () => {
    if (!selected) return null;
    const st2 = getSt(selected.status);
    return (
      <Modal visible={showDetail} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowDetail(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
          <View style={s.modalH}><TouchableOpacity onPress={() => setShowDetail(false)}><Ionicons name="close" size={24} color="#64748B" /></TouchableOpacity>
            <Text style={s.modalTitle}>{selected.name}</Text><View style={{ width: 24 }} /></View>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            {/* Open Dashboard Button */}
            <TouchableOpacity style={[s.submitBtn, { marginTop: 0, marginBottom: 16 }]} onPress={() => openClientDashboard(selected)}>
              <MaterialCommunityIcons name="view-dashboard" size={18} color="#FFF" />
              <Text style={s.submitBtnTxt}>Open Client Dashboard</Text>
              <Ionicons name="chevron-forward" size={16} color="#FFF" />
            </TouchableOpacity>

            <View style={s.card}>
              <Text style={s.secTitle}>Client Details</Text>
              {[{ l: 'Name', v: selected.name }, { l: 'Company', v: selected.company },
                { l: 'Email', v: selected.email }, { l: 'Phone', v: selected.phone },
                { l: 'Industry', v: selected.industry }, { l: 'City', v: selected.city },
                { l: 'Status', v: st2.l }, { l: 'Client #', v: selected.client_number },
                { l: 'VAT', v: selected.vat_number }, { l: 'Website', v: selected.website },
                { l: 'Projects', v: selected.project_count ? String(selected.project_count) : '0' },
                { l: 'Notes', v: selected.notes },
              ].filter(r => r.v).map((r, i) => (<View key={i} style={s.row}><Text style={s.rowLbl}>{r.l}</Text><Text style={s.rowVal}>{r.v}</Text></View>))}
            </View>

            <TouchableOpacity style={[s.submitBtn, { backgroundColor: '#EF4444' }]} onPress={() => handleDeleteClient(selected)}>
              <MaterialCommunityIcons name="account-off" size={18} color="#FFF" /><Text style={s.submitBtnTxt}>Deactivate Client</Text></TouchableOpacity>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ══ MAIN ══
  if (isLoading) return (<SafeAreaView style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
    <StatusBar barStyle="dark-content" /><ActivityIndicator size="large" color="#0e4e96" /><Text style={{ color: '#64748B', marginTop: 12 }}>Loading clients...</Text></SafeAreaView>);

  return (<SafeAreaView style={{ flex: 1, backgroundColor: '#0e4e96' }}>
    <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
    {renderHeader()}{renderKPIs()}{renderTabs()}
    <ScrollView style={{ flex: 1 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />} showsVerticalScrollIndicator={false}>
      {renderContent()}<View style={{ height: 40 }} /></ScrollView>
    {renderAddClientModal()}{renderDetailModal()}
  </SafeAreaView>);
}

const s = StyleSheet.create({
  header: { paddingTop: 8, paddingBottom: 12, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn: { width: 36, height: 36, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  hTitle: { fontSize: 18, fontWeight: '800', color: '#FFF' },
  kpiBar: { flexGrow: 0, backgroundColor: '#FFF', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  kpiItem: { alignItems: 'center', marginRight: 14, minWidth: 60 },
  kpiVal: { fontSize: 14, fontWeight: '800', marginTop: 2 },
  kpiLbl: { fontSize: 10, color: '#94A3B8', marginTop: 1 },
  tabsRow: { backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 10, borderRadius: 14, marginRight: 2 },
  activeTab: { backgroundColor: '#EFF6FF' },
  tabTxt: { fontSize: 12, color: '#94A3B8', marginLeft: 4, fontWeight: '500' },
  searchRow: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#F1F5F9', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 8, marginRight: 8 },
  searchInput: { flex: 1, fontSize: 14, color: '#0F172A', marginLeft: 6 },
  addBtn: { width: 40, height: 40, borderRadius: 12, backgroundColor: '#0e4e96', justifyContent: 'center', alignItems: 'center' },
  card: { backgroundColor: '#FFF', marginHorizontal: 16, marginBottom: 8, padding: 14, borderRadius: 12, borderWidth: 1, borderColor: '#E2E8F0' },
  avatar: { width: 42, height: 42, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  avatarTxt: { color: '#FFF', fontWeight: '700', fontSize: 15 },
  cardName: { fontSize: 15, fontWeight: '700', color: '#1E293B' },
  cardMeta: { fontSize: 12, color: '#94A3B8', marginTop: 1 },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeTxt: { fontSize: 10, fontWeight: '600' },
  dashBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 10, paddingVertical: 8, backgroundColor: '#EFF6FF', borderRadius: 8, gap: 6 },
  dashBtnTxt: { fontSize: 13, color: '#0e4e96', fontWeight: '600' },
  secTitle: { fontSize: 16, fontWeight: '800', color: '#0F172A', marginBottom: 10 },
  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  rowLbl: { fontSize: 13, color: '#64748B', fontWeight: '500' },
  rowVal: { fontSize: 14, fontWeight: '700', color: '#1E293B', flex: 1, textAlign: 'right' },
  chip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 14, backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: '#E2E8F0', marginRight: 6 },
  chipActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  chipTxt: { fontSize: 12, color: '#64748B', fontWeight: '500' },
  modalH: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#E2E8F0', backgroundColor: '#FFF' },
  modalTitle: { fontSize: 17, fontWeight: '700', color: '#0F172A', flex: 1, textAlign: 'center' },
  formLbl: { fontSize: 13, fontWeight: '600', color: '#334155', marginBottom: 6, marginTop: 10 },
  formInput: { backgroundColor: '#FFF', borderWidth: 1.5, borderColor: '#E2E8F0', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, color: '#0F172A', marginBottom: 4 },
  submitBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 14, marginTop: 16, gap: 6 },
  submitBtnTxt: { color: '#FFF', fontSize: 16, fontWeight: '700' },
});

